import { makePostRequest } from "@redotech/redo-shopify-utils/request-helper";
import {
  type CoverageLoadedEvent,
  type CoveragePlacement,
  coveragePlacementId,
  coverageTreatmentKey,
} from "@redotech/redo-storefront-common/coverage-placement-metrics";
import { WidgetElementType } from "@redotech/redo-storefront-common/widget-config";
import type {
  ResolvedWidgetPlacement,
  WidgetAutocheck,
} from "@redotech/redo-storefront-widget-common/widget";
import { LRUCache } from "@redotech/util/cache";
import { localStore } from "@redotech/web-util/storage";
import { inCoverageBuilderMode } from "./coverage-builder/coverage-builder-init";
import { log } from "./monitoring/log";
import { PreviewModeState } from "./preview-mode/preview-state";

const MINUTE_MS = 60_000;
const STORAGE_PREFIX = "redo_coverage_observation:";
const inFlight = new Set<string>();
const localReports = new LRUCache<
  string,
  { attemptedAt: number; reportedAt?: number }
>(1000);

interface ReportingContext {
  autocheck: Pick<WidgetAutocheck, "widgetSlug" | "coveragePlacementMetrics">;
  treatmentId: string | null;
}

interface PlacedCoverageWidget {
  redoHtmlElements: readonly { isConnected: boolean; root: ParentNode }[];
  widgetConfig?: { elementType: WidgetElementType };
  placement: ResolvedWidgetPlacement;
}

const renderedSelectors: Record<WidgetElementType, string> = {
  [WidgetElementType.COVERAGE_CHECKOUT_BUTTON]:
    '[data-target="coverage-button"]',
  [WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON]:
    '[data-target="non-coverage-button"]',
  [WidgetElementType.BOTH_CHECKOUT_BUTTONS]:
    '[data-target="coverage-button"], [data-target="non-coverage-button"]',
  [WidgetElementType.INFO_CARD]: '[data-target="info-card-container"]',
  [WidgetElementType.SINGLE_TOGGLE]: '[data-target="toggle-root"]',
  [WidgetElementType.DOUBLE_TOGGLE]: '[data-target="toggle-root"]',
  [WidgetElementType.CART_CARD]: '[data-target="card-container"]',
  [WidgetElementType.CART_CHECKBOX]: '[data-target="cart-checkbox-root"]',
  [WidgetElementType.CHECKOUT_BUTTON]:
    '[data-target="coverage-button"], [data-target="non-coverage-button"]',
};

function isRenderedWidgetElement(
  element: { isConnected: boolean; root: ParentNode },
  elementType: WidgetElementType,
): boolean {
  return (
    element.isConnected &&
    element.root.querySelector(renderedSelectors[elementType]) !== null
  );
}

/** Widget elements that are connected and have mounted their inner content. */
export function getRenderedWidgetElements<
  TElement extends { isConnected: boolean; root: ParentNode },
>(
  widgets: readonly {
    redoHtmlElements: readonly TElement[];
    widgetConfig?: { elementType: WidgetElementType };
  }[],
  standardElementType: WidgetElementType,
): TElement[] {
  return widgets.flatMap((widget) => {
    const elementType = widget.widgetConfig?.elementType ?? standardElementType;
    return widget.redoHtmlElements.filter((element) =>
      isRenderedWidgetElement(element, elementType),
    );
  });
}

export function getObservedCoveragePlacements(
  widgets: readonly PlacedCoverageWidget[],
  standardElementType: WidgetElementType,
): CoveragePlacement[] {
  const observed: CoveragePlacement[] = [];
  for (const widget of widgets) {
    const elementType = widget.widgetConfig?.elementType ?? standardElementType;
    if (
      !widget.redoHtmlElements.some((element) =>
        isRenderedWidgetElement(element, elementType),
      )
    ) {
      continue;
    }
    observed.push({
      elementType,
      selector: widget.placement.cssSelector,
      insertionPosition: widget.placement.insertionPosition,
      ...(widget.placement.shadowRootSelector
        ? { shadowRootSelector: widget.placement.shadowRootSelector }
        : {}),
    });
  }
  return observed;
}

export function shouldReport(minutesSinceLastSeen: number): boolean {
  if (minutesSinceLastSeen < 1) {
    return false;
  }
  if (minutesSinceLastSeen >= 30 || Number.isNaN(minutesSinceLastSeen)) {
    return true;
  }
  return Math.random() < 0.01 + (0.99 * (minutesSinceLastSeen - 1)) / 29;
}

function reportingData({ autocheck, treatmentId }: ReportingContext) {
  const theme = globalThis.Shopify?.theme;
  if (
    !autocheck.widgetSlug ||
    !theme?.id ||
    theme.role !== "main" ||
    globalThis.Shopify?.designMode ||
    inCoverageBuilderMode() ||
    PreviewModeState.isActive()
  ) {
    return undefined;
  }
  const base =
    globalThis.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL;
  if (!base) {
    return undefined;
  }
  const themeId = String(theme.id);
  const path = location.pathname;
  const pageType: CoverageLoadedEvent["pageType"] = /\/products(?:\/|$)/.test(
    path,
  )
    ? "pdp"
    : /\/collections(?:\/|$)/.test(path)
      ? "collection"
      : /\/cart(?:\/|$)/.test(path)
        ? "cart"
        : "unknown";
  return {
    url: `${base.replace(/\/$/, "")}/widgets/${encodeURIComponent(autocheck.widgetSlug)}`,
    key: [autocheck.widgetSlug, themeId, treatmentId],
    history:
      autocheck.coveragePlacementMetrics?.[themeId]?.[
        coverageTreatmentKey(treatmentId)
      ],
    event: { themeId, treatmentId, url: location.href, path, pageType },
  };
}

function startReport(key: string, serverLastSeen: string | undefined): boolean {
  if (inFlight.has(key)) {
    return false;
  }
  const now = Date.now();
  const local = localReports.get(key);
  if (local && now - local.attemptedAt < MINUTE_MS) {
    return false;
  }
  const stored = Number(localStore(STORAGE_PREFIX + key).get());
  const serverSeen = serverLastSeen ? Date.parse(serverLastSeen) : 0;
  const lastSeen = Math.max(
    Number.isFinite(serverSeen) ? serverSeen : 0,
    Number.isFinite(stored) ? stored : 0,
    local?.reportedAt ?? 0,
  );
  localReports.set(key, { attemptedAt: now, reportedAt: local?.reportedAt });
  if (!shouldReport(lastSeen ? (now - lastSeen) / MINUTE_MS : Infinity)) {
    return false;
  }
  inFlight.add(key);
  return true;
}

async function sendReport(
  url: string,
  body: object,
  keys: string[],
): Promise<void> {
  try {
    const response = await makePostRequest(url, body);
    if (!response.ok) {
      throw new Error(`Coverage observation returned ${response.status}`);
    }
    const now = Date.now();
    for (const key of keys) {
      localReports.set(key, { attemptedAt: now, reportedAt: now });
      localStore(STORAGE_PREFIX + key).set(String(now));
    }
  } catch (error) {
    log("Failed to report coverage observation", error);
  } finally {
    for (const key of keys) {
      inFlight.delete(key);
    }
  }
}

export async function reportCoverageLoaded(
  context: ReportingContext,
): Promise<void> {
  try {
    const data = reportingData(context);
    if (!data) {
      return;
    }
    const key = JSON.stringify([...data.key, "loaded"]);
    if (startReport(key, data.history?.lastLoadedAt)) {
      await sendReport(`${data.url}/coverage-loaded-event`, data.event, [key]);
    }
  } catch (error) {
    log("Failed to prepare coverage loaded observation", error);
  }
}

export async function reportCoveragePlacementMetrics(
  context: ReportingContext,
  placements: CoveragePlacement[],
): Promise<void> {
  try {
    const data = reportingData(context);
    if (!data) {
      return;
    }
    const unique = [
      ...new Map(placements.map((p) => [coveragePlacementId(p), p])).values(),
    ];
    const due = unique.filter((placement) => {
      const id = coveragePlacementId(placement);
      return startReport(
        JSON.stringify([...data.key, id]),
        data.history?.placements[id]?.lastSeenAt,
      );
    });
    for (let i = 0; i < due.length; i += 50) {
      const batch = due.slice(i, i + 50);
      await sendReport(
        `${data.url}/coverage-placement-event`,
        { ...data.event, placements: batch },
        batch.map((p) => JSON.stringify([...data.key, coveragePlacementId(p)])),
      );
    }
  } catch (error) {
    log("Failed to prepare coverage placement observation", error);
  }
}
